import { ReactNode, useContext, useEffect, useState } from 'react';
import { createContext } from 'react';
import { SnapshotTypeEnum } from '../../../model/snapshot_type';
import { GetVolContext } from '../../get-vol-provider';
import { CurrencyTypeEnum } from '../../../model/currency_type.enum';
import usePersistentState from '../../use-persistent-state';

export enum VolSmileDataType {
  volSmile = 'vol-smile',
  termStructure = 'term-structure',
  volatilitySurface = 'volatility-surface',
  scatterPlot = 'scatter-plot',
}

interface IParamsProvider {
  children: ReactNode;
  dataType: VolSmileDataType;
  providerKey: string;
}

interface IProvider {
  socketEvent: any;
  type: SnapshotTypeEnum;
  setType: (value: SnapshotTypeEnum) => void;
  token: CurrencyTypeEnum;
  setToken: (value: CurrencyTypeEnum) => void;
  currentTimeStamp: string;
  tenors?: Record<string, any>;
  tenorKeys: string[];
  hasDefaultToken: boolean;
}

const TYPES = {
  [VolSmileDataType.volSmile]: SnapshotTypeEnum.EE,
  [VolSmileDataType.termStructure]: SnapshotTypeEnum.EE,
  [VolSmileDataType.volatilitySurface]: SnapshotTypeEnum.EE,
  [VolSmileDataType.scatterPlot]: SnapshotTypeEnum.CM,
};

export const VolSmileContext = createContext<IProvider>({} as IProvider);

export const VolSmileProvider = ({ dataType, children, providerKey }: IParamsProvider) => {
  const [type, setType] = usePersistentState<SnapshotTypeEnum>(
    [providerKey, 'VOL_SMILE', dataType],
    'type',
    TYPES[dataType],
  );

  const {
    data,
    timestamp,
    currency: token,
    setCurrency: setToken,
    hasDefaultToken,
  } = useContext(GetVolContext);

  const socketEvent = data[type];
  const currentTimeStamp = timestamp[type];

  const [tenors, setTenors] = useState<Record<string, any> | undefined>(undefined);
  const [tenorKeys, setTenorKeys] = useState<string[]>([]);

  useEffect(() => {
    const extractTenors = (evt: any) => {
      if (!evt) return undefined;
      if (evt?.params) return evt?.params?.data?.tenors as Record<string, any> | undefined;
      if (evt?.result) return evt?.result?.tenors as Record<string, any> | undefined;
      return undefined;
    };

    const nextTenors = extractTenors(socketEvent);
    setTenors(nextTenors);

    const nextKeys = nextTenors ? Object.keys(nextTenors) : [];
    setTenorKeys((prev) => {
      if (prev.length === nextKeys.length && prev.every((k) => nextKeys.includes(k))) {
        return prev;
      }
      return nextKeys;
    });
  }, [socketEvent]);

  return (
    <VolSmileContext.Provider
      value={{
        socketEvent,
        type,
        setType,
        token,
        setToken,
        currentTimeStamp,
        tenors,
        tenorKeys,
        hasDefaultToken,
      }}
    >
      {children}
    </VolSmileContext.Provider>
  );
};
